import React, { Component } from "react";
import ReactHtmlParser from "react-html-parser";
import { AxiosResponse } from "axios";
import { CommentResource } from "../../../../stores/CommentStore/Types";
import { CurrentUser } from "../../../../stores/CurrentUserStore/Types";
import { postRequest, deleteRequest } from "../../../../utils/httpRequest";
import { errorNotification } from "../../../../utils/helpers";
import Comment from "./Comment";

interface Props {
  currentUser: CurrentUser;
  conversation: CommentResource;
  enrolmentId: number;
  questionId: number;
  updateComments: () => void;
}

interface State {
  reply: string;
  showReplyForm: boolean;
  liking: boolean;
  replying: boolean;
  viewReplies: boolean;
  deleting: boolean;
}

class Conversation extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      reply: "",
      showReplyForm: false,
      liking: false,
      replying: false,
      viewReplies: false,
      deleting: false,
    };
  }

  handleShowReplyForm = (e) => {
    e.preventDefault();
    this.setState({ showReplyForm: !this.state.showReplyForm });
  };

  handleViewReplies = (e) => {
    e.preventDefault();
    this.setState({ viewReplies: !this.state.viewReplies });
  };

  likeComment = (e, commentId) => {
    e.preventDefault();
    this.setState({ liking: true });

    const {
      conversation: { liker_ids },
      currentUser,
    } = this.props;

    if (liker_ids && liker_ids.includes(currentUser?.id)) {
      return this.unlikeComment(commentId);
    }

    return postRequest(`/api/likes.json?comment_id=${commentId}`, {})
      .then((response: AxiosResponse) => {
        this.setState({ liking: false });
        this.props.updateComments();
      })
      .catch((error) => {
        this.setState({ liking: false });

        errorNotification(
          "Error liking comment",
          JSON.stringify(error.response.data),
        );
      });
  };

  unlikeComment = (commentId) => {
    return postRequest(`/api/likes/unlike.json?comment_id=${commentId}`, {})
      .then((response: AxiosResponse) => {
        this.setState({ liking: false });
        this.props.updateComments();
      })
      .catch((error) => {
        this.setState({ liking: false });

        errorNotification(
          "Error unliking comment",
          JSON.stringify(error.response.data),
        );
      });
  };

  replyComment = (e, commentId) => {
    e.preventDefault();
    const { enrolmentId, questionId } = this.props;

    this.setState({ replying: true });

    return postRequest(`/api/comments.json?comment_id=${commentId}`, {
      content: this.state.reply,
      enrolment_id: enrolmentId,
      question_id: questionId,
    })
      .then((response: AxiosResponse) => {
        this.setState({ reply: "", replying: false, viewReplies: true });
        this.props.updateComments();
      })
      .catch((error) => {
        this.setState({ reply: "", replying: false });

        errorNotification(
          "Error replying comment",
          JSON.stringify(error.response.data),
        );
      });
  };

  deleteComment = (e) => {
    e.preventDefault();
    this.setState({ deleting: true });

    const {
      conversation: { id },
      questionId,
    } = this.props;

    return deleteRequest(`/api/comments/${id}.json?question_id=${questionId}`)
      .then((response: AxiosResponse) => {
        this.props.updateComments();
      })
      .catch((error) => {
        this.setState({ deleting: false });

        errorNotification(
          "Error deleting comment",
          JSON.stringify(error.response.data),
        );
      });
  };

  setReply = (e) => {
    this.setState({ reply: e.target.value });
  };

  countryFlagImage = (countryCode) => {
    let img;
    try {
      img = require(
        `../../../../../../assets/images/flags/svg/${countryCode?.toLowerCase()}.svg`,
      );
    } catch {
      img = "";
    }

    return img;
  };

  likeClasses = () => {
    const { liking } = this.state;
    const {
      conversation: { liker_ids, parent },
      currentUser,
    } = this.props;

    let classes = [];
    if (liking) {
      classes.push("loading");
    }

    if (liker_ids.includes(currentUser?.id)) {
      classes.push("liked");
    }
    if (parent == "Question") {
      classes.push("repliable");
    }
    return classes.join(" ");
  };

  initials = () => {
    const { user } = this.props.conversation;
    const name =
      user?.short_name_split || user?.short_name || user?.protected_name;

    return (
      name
        ?.split(" ")
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase() || "42"
    );
  };

  render() {
    const {
      conversation: {
        id,
        user,
        created_at,
        posted_on,
        formatted_content,
        likes_count,
        comments,
        parent,
        liker_ids,
      },
      conversation,
      currentUser,
      updateComments,
      enrolmentId,
      questionId,
    } = this.props;

    const { reply, showReplyForm, replying, viewReplies, deleting } =
      this.state;
    const liked = liker_ids.includes(currentUser?.id);
    const canReply = parent == "Question";

    return (
      <div className="comment conversation" id={`comment_${id}`}>
        {deleting && (
          <div className="ui active inverted dimmer">
            <div className="ui text loader">Loading</div>
          </div>
        )}
        <span className="av-c">
          {user?.profile_picture_url || user?.profile_picture ? (
            <img
              src={user.profile_picture_url || user.profile_picture}
              alt=""
            />
          ) : (
            this.initials()
          )}
        </span>
        {/* The full date rides on the whole comment as a tooltip, so a rest of
            the mouse anywhere on it answers "when was this?" */}
        <div className="comment__body" title={posted_on}>
          <div className="comment__top">
            <span className="comment__name">{user?.protected_name}</span>
            {user?.country && (
              <img
                className="flag"
                src={this.countryFlagImage(user.country)}
                title={user.country_name}
                alt={user.country_name || user.country}
              />
            )}
            {created_at ? (
              <span className="comment__time">{created_at}</span>
            ) : (
              <span className="comment__time comment__time--on-hover">
                {posted_on}
              </span>
            )}
          </div>
          {/* @ts-ignore */}
          <div className="comment__text">
            {ReactHtmlParser(formatted_content)}
          </div>
          <div className="comment__actions">
            <button
              className={this.likeClasses()}
              type="button"
              aria-pressed={liked}
              onClick={(e) => this.likeComment(e, id)}
            >
              <svg aria-hidden="true" viewBox="0 0 24 24">
                <path d="M12 21C7 16.5 3 13 3 8.6 3 5.9 5.2 4 7.5 4 9.5 4 11 5.3 12 7 13 5.3 14.5 4 16.5 4 18.8 4 21 5.9 21 8.6 21 13 17 16.5 12 21Z" />
              </svg>
              {likes_count}
            </button>
            {canReply && (
              <button type="button" onClick={this.handleShowReplyForm}>
                <svg aria-hidden="true" viewBox="0 0 24 24">
                  <path d="M9 7 4 12l5 5M4 12h11a5 5 0 0 1 5 5v1" />
                </svg>
                Reply
              </button>
            )}
            {comments?.length > 0 && canReply && (
              <button
                className="replies-link"
                type="button"
                aria-expanded={viewReplies}
                onClick={this.handleViewReplies}
              >
                {viewReplies ? "Hide replies" : "View replies"}
              </button>
            )}
            {(user?.id === currentUser?.id || currentUser?.admin) && (
              <button type="button" onClick={this.deleteComment}>
                Delete
              </button>
            )}
          </div>
          {viewReplies && (
            <div className="replies is-open">
              {comments.map((comment) => (
                <Comment
                  key={comment.id}
                  comment={comment}
                  currentUser={currentUser}
                  updateComments={updateComments}
                  commentType="conversation"
                  commentableId={conversation.id}
                  enrolmentId={enrolmentId}
                  questionId={questionId}
                />
              ))}
            </div>
          )}
          {showReplyForm && (
            <form
              className="reply__body"
              onSubmit={(e) => this.replyComment(e, id)}
            >
              <span className="av-c">
                {currentUser?.profile_picture ||
                currentUser?.profile_picture_url ? (
                  <img
                    src={
                      currentUser.profile_picture ||
                      currentUser.profile_picture_url
                    }
                    alt=""
                  />
                ) : (
                  currentUser?.short_name?.slice(0, 2).toUpperCase()
                )}
              </span>
              <div className="reply__body--content">
                <div className="who">{currentUser?.short_name} (you)</div>
                <textarea
                  value={reply}
                  rows={3}
                  placeholder="Write your reply"
                  onChange={this.setReply}
                />
                <div className="action">
                  <button
                    className={`btn btn-blue btn--compact ${
                      replying ? "loading" : ""
                    }`}
                    type="submit"
                    disabled={replying || !reply.trim()}
                  >
                    Post
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }
}

export default Conversation;
